import { noop } from 'lodash';
import { LoadableActionResult } from '@higherlogic/ocfe/src/utilHooks';
import { DataLoadable, isLoadSuccess, isLoading, loadError, loadSuccess, loading } from '@higherlogic/ocfe/src/utils';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { BioBubbleContact, getBioBubbleContact } from 'src/BioBubble/api';

export type BiobubbleUpdateProcessor = (data: BioBubbleContact) => BioBubbleContact;

export type BioBubbleContextValue = readonly [
    Record<string, DataLoadable<BioBubbleContact>>,
    (key: string) => void,
    (key: string, processor: BiobubbleUpdateProcessor) => void
];

export const GlobalBioBubbleContext = React.createContext<BioBubbleContextValue>([{}, noop, noop]);

const loadingPlaceholder = loading();

export const useBioBubbleContactLoadable = (contactKey: string): LoadableActionResult<BioBubbleContact, []> => {
    const [loadables, loadByKey, updateByKey] = useContext(GlobalBioBubbleContext);
    const loadable = loadables[contactKey] ?? loadingPlaceholder;
    const load = useCallback(() => loadByKey(contactKey), [contactKey, loadByKey]);
    const update = useCallback(
        (processor: BiobubbleUpdateProcessor) => updateByKey(contactKey, processor),
        [contactKey, updateByKey]
    );
    return useMemo(() => [loadable, load, update, noop], [load, loadable, update]);
};

GlobalBioBubbleContext.displayName = 'GlobalBioBubbleContext';

const GlobalBioBubbleProvider: React.FCC = ({ children }) => {
    const [loadableMap, setLoadableMap] = useState<Record<string, DataLoadable<BioBubbleContact>>>({});
    const setByKey = useCallback((contactKey: string, data: DataLoadable<BioBubbleContact>) => {
        setLoadableMap((m) => ({ ...m, [contactKey]: data }));
    }, []);

    const loadByContact = useCallback(
        (contactKey: string) => {
            const contactLoadable = loadableMap[contactKey];
            if (contactLoadable && (isLoadSuccess(contactLoadable) || isLoading(contactLoadable))) {
                return;
            }
            setByKey(contactKey, loading(null));
            getBioBubbleContact(contactKey).then(
                (data) => {
                    setByKey(contactKey, loadSuccess(data));
                },
                (error: Error) => {
                    setByKey(contactKey, loadError(error, null));
                }
            );
        },
        [loadableMap, setByKey]
    );

    const updateByContact = useCallback(
        (contactKey: string, processor: BiobubbleUpdateProcessor) => {
            const contactLoadable = loadableMap[contactKey];
            if (contactLoadable && isLoadSuccess(contactLoadable)) {
                setByKey(contactKey, loadSuccess(processor(contactLoadable.data)));
            }
        },
        [loadableMap, setByKey]
    );

    const providerValue = useMemo(
        () => [loadableMap, loadByContact, updateByContact] as const,
        [loadableMap, loadByContact, updateByContact]
    );

    return <GlobalBioBubbleContext.Provider value={providerValue}>{children}</GlobalBioBubbleContext.Provider>;
};

export default GlobalBioBubbleProvider;
